import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {QrCode} from "types/QrCode";
import {useNotifications} from "context/NotificationContext";
import {useOperator} from "context/OperatorContext";
import Button from "components/Button";
import {PencilIcon, TrashIcon, UserIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import {formattedDate} from "services/date";
import StackedList from "components/StackedList";
import StackedListItem from "components/StackedListItem";
import {QrcodeIcon} from "@heroicons/react/outline";
import DeleteQrCode from "pages/QrCode/Delete";
import Link from "components/Link";

function ViewQrCode() {
  const {id} = useParams()
  const {data: qrCode, isLoading} = useQuery(
    ['qr-code', {id}],
    () => ngage.qrCodes.get(parseInt(id as string))
  )

  const [deletingQrCode, setDeletingQrCode] = useState<QrCode | null>(null);
  const {notify} = useNotifications()
  const navigate = useNavigate()
  const {subdomain} = useOperator()

  // Prepare action handlers
  function handleDeleteQrCode() {
    if (qrCode) setDeletingQrCode(qrCode)
  }

  function handleEditQrCode() {
    if (id) navigate(`/qr-codes/${id}/edit`)
  }

  // Prepare page actions
  const actions = <>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteQrCode}
            disabled={!qrCode || qrCode.trigger_count > 0}>Delete</Button>
    <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditQrCode}>Edit</Button>
  </>

  return (
    <Page
      title={qrCode?.name || "View QR Code"}
      breadcrumbs={{"Hardware": "/", "QR Codes": "/qr-codes", [qrCode?.name || "View QR Code"]: `/qr-codes/${id}`}}
      actions={actions}
      isLoading={isLoading}
    >
      {qrCode ?
        <div className="lg:grid lg:grid-cols-3 lg:gap-4">
          {/* QR Code Information */}
          <section className="lg:col-span-2">
            <Card heading="QR Code Information">
              <DescriptionList>
                <DescriptionListItem span={1}>
                  <DescriptionTerm>Topic</DescriptionTerm>
                  <DescriptionDetails>
                    {qrCode.topic?.name ?? 'N/A'}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Created</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedDate(qrCode.created_at)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Files</DescriptionTerm>
                  <DescriptionDetails>
                    <StackedList>
                      <StackedListItem
                        icon={QrcodeIcon}
                        action={
                        <Link to={ngage.qrCodes.downloadUrl(qrCode.id, "png", 1000, subdomain)} download>Download</Link>
                      }>
                        PNG file (1000px square)
                      </StackedListItem>
                      <StackedListItem
                        icon={QrcodeIcon}
                        action={
                        <Link to={ngage.qrCodes.downloadUrl(qrCode.id, "svg", 500, subdomain)} download>Download</Link>
                      }>
                        SVG file {qrCode.appearance.is_branded && '(No branding)'}
                      </StackedListItem>
                      <StackedListItem
                        icon={QrcodeIcon}
                        action={
                        <Link to={ngage.qrCodes.downloadUrl(qrCode.id, "eps", 500, subdomain)} download>Download</Link>
                      }>
                        EPS file {qrCode.appearance.is_branded && '(No branding)'}
                      </StackedListItem>
                    </StackedList>
                  </DescriptionDetails>
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </section>

          <section className="mt-4 lg:mt-0 lg:col-span-1">
            <Card heading="Preview">
              <div className="relative flex justify-center items-center overflow-hidden">
                <img
                  src={qrCode.preview}
                  alt={`${qrCode.name} preview`}
                  className="z-10"
                />
                <UserIcon className="absolute top-16 lg:top-12 left-0 right-0 h-auto text-gray-200"/>
              </div>
            </Card>
          </section>
        </div>
        : null}

      {/* Delete confirmation modal */}
      <DeleteQrCode
        qrCode={deletingQrCode}
        show={qrCode === deletingQrCode}
        onCancel={() => setDeletingQrCode(null)}
        onDelete={qrCode => {
          navigate('/qr-codes')
          notify("success", "QR Code Deleted", `${qrCode.name} has been permanently removed`)
        }}
        onDeleteFail={qrCode => {
          setDeletingQrCode(null)
          notify("error", "QR Code Not Deleted", `${qrCode.name} could not be removed`)
        }}
      />
    </Page>
  );
}

export default ViewQrCode;